import type { LogLevel, Logger, Unsubscribe } from '../core/services';

export class Emitter<Events extends object> {
  private readonly listeners = new Map<keyof Events, Set<(payload: never) => void>>();

  on<K extends keyof Events>(event: K, listener: (payload: Events[K]) => void): Unsubscribe {
    let set = this.listeners.get(event);
    if (!set) {
      set = new Set();
      this.listeners.set(event, set);
    }
    set.add(listener as (payload: never) => void);
    return () => set.delete(listener as (payload: never) => void);
  }

  emit<K extends keyof Events>(event: K, payload: Events[K]): void {
    const set = this.listeners.get(event);
    if (!set) return;
    for (const listener of [...set]) {
      try {
        (listener as (payload: Events[K]) => void)(payload);
      } catch (error) {
        console.error(`[studio] listener for "${String(event)}" threw`, error);
      }
    }
  }

  clear(): void {
    this.listeners.clear();
  }
}

export interface LogEntry {
  at: number;
  level: LogLevel;
  scope: string;
  message: string;
}

/** Console logger that also keeps the most recent entries for the diagnostics view. */
export function createLogger(limit = 500): Logger & { entries(): readonly LogEntry[] } {
  const buffer: LogEntry[] = [];
  return {
    log(level, scope, message, detail) {
      buffer.push({ at: Date.now(), level, scope, message });
      if (buffer.length > limit) buffer.splice(0, buffer.length - limit);
      const line = `[${scope}] ${message}`;
      if (detail === undefined) console[level](line);
      else console[level](line, detail);
    },
    entries: () => buffer,
  };
}

export function errorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return typeof error === 'string' ? error : 'Unknown error';
}

export function shallowEqual<T extends object>(a: T, b: T): boolean {
  const keysA = Object.keys(a) as Array<keyof T>;
  if (keysA.length !== Object.keys(b).length) return false;
  return keysA.every((key) => Object.is(a[key], b[key]));
}
